import React, { useState, useRef, useEffect } from "react"
import { Head } from "@inertiajs/react"
import { router } from "@inertiajs/react"
import { AuthenticatedLayout } from "@/components/layout/authenticated-layout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Plus, Edit, Trash2, Eye, MessageSquare, Code, Settings, Copy, X, Info, MoreVertical } from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import VariableInsertButton from "@/components/shared/VariableInsertButton"
import TemplateVariablesTab from "@/components/shared/TemplateVariablesTab"
import {
    TEXT_TEMPLATE_CATEGORIES,
    TEXT_TEMPLATE_VARIABLES,
    parseVariablesFromContent,
    renderPreviewContent,
    estimateSmsSegments,
    getSampleVariableData,
} from "@/lib/textTemplateVariables"
import {
    splitCustomVariables,
    mergeTemplateVariables,
    buildInsertVariableList,
    buildSampleDataFromVariables,
} from "@/lib/templateVariableHelpers"
import {
    TEMPLATE_MODAL_OVERLAY_CLASS,
    TEXT_TEMPLATE_EDITOR_DIALOG_CLASS,
    TEXT_TEMPLATE_PREVIEW_DIALOG_CLASS,
    TEMPLATE_MODAL_TABS_LIST_CLASS,
    TEMPLATE_MODAL_TABS_TRIGGER_CLASS,
    TEMPLATE_MODAL_SELECT_CONTENT_CLASS,
} from "@/lib/templateModalStyles"

export default function TextTemplates({ templates = [], categories = TEXT_TEMPLATE_CATEGORIES, variableCatalog = TEXT_TEMPLATE_VARIABLES }) {
    const { toast } = useToast()

    // Load saved filters from localStorage on component mount
    const loadSavedFilters = () => {
        try {
            const savedFilters = localStorage.getItem('text-templates-filters');
            if (savedFilters) {
                const filters = JSON.parse(savedFilters);
                return {
                    searchTerm: filters.searchTerm || "",
                    statusFilter: filters.statusFilter || "All Statuses",
                    categoryFilter: filters.categoryFilter || "All Categories",
                };
            }
        } catch (error) {
            console.error('Error loading saved filters:', error);
        }
        
        // Return default values if no saved filters or error
        return {
            searchTerm: "",
            statusFilter: "All Statuses",
            categoryFilter: "All Categories",
        };
    };

    // Save filters to localStorage
    const saveFilters = (filters) => {
        try {
            localStorage.setItem('text-templates-filters', JSON.stringify(filters));
        } catch (error) {
            console.error('Error saving filters:', error);
        }
    };

    // Load initial filter values
    const initialFilters = loadSavedFilters();

    const [selectedTemplate, setSelectedTemplate] = useState(null)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isPreviewOpen, setIsPreviewOpen] = useState(false)
    const [previewContent, setPreviewContent] = useState(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [searchTerm, setSearchTerm] = useState(initialFilters.searchTerm)
    const [statusFilter, setStatusFilter] = useState(initialFilters.statusFilter)
    const [categoryFilter, setCategoryFilter] = useState(initialFilters.categoryFilter)
    const [isDuplicating, setIsDuplicating] = useState(false)
    const [livePreviewContent, setLivePreviewContent] = useState(null)
    const [customVariables, setCustomVariables] = useState([])
    const [isSavingVariables, setIsSavingVariables] = useState(false)
    
    const [formData, setFormData] = useState({
        name: "",
        key: "",
        content: "",
        description: "",
        category: "workflow",
        variables: [],
        media_urls: [],
        is_active: true,
    })

    // Save filters whenever they change
    useEffect(() => {
        const filtersToSave = {
            searchTerm,
            statusFilter,
            categoryFilter,
        };
        saveFilters(filtersToSave);
    }, [searchTerm, statusFilter, categoryFilter]);

    // Handle ?action=new URL parameter to open create form from quick actions menu
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('action') === 'new') {
            openModal();
            // Clean up URL
            router.replace(window.location.pathname, { preserveState: true, preserveScroll: true });
        }
    }, []);

    // Reset all filters
    const resetFilters = () => {
        setSearchTerm("");
        setStatusFilter("All Statuses");
        setCategoryFilter("All Categories");
    };

    // Count active filters
    const getActiveFilterCount = () => {
        let count = 0;
        if (searchTerm) count++;
        if (statusFilter !== "All Statuses") count++;
        if (categoryFilter !== "All Categories") count++;
        return count;
    };

    const insertVariables = React.useMemo(
        () => buildInsertVariableList(variableCatalog, customVariables),
        [variableCatalog, customVariables],
    )

    const isEditing = !!selectedTemplate?.id

    const filteredTemplates = templates.filter(template => {
        const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            template.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (template.content && template.content.toLowerCase().includes(searchTerm.toLowerCase()))
        
        const matchesStatus = statusFilter === "All Statuses" || 
            (statusFilter === "Active" && template.is_active) ||
            (statusFilter === "Inactive" && !template.is_active)

        const matchesCategory = categoryFilter === "All Categories" ||
            template.category === categoryFilter
        
        return matchesSearch && matchesStatus && matchesCategory
    })

    const resetForm = () => {
        setFormData({
            name: "",
            key: "",
            content: "",
            description: "",
            category: "workflow",
            variables: [],
            media_urls: [],
            is_active: true,
        })
        setCustomVariables([])
        setSelectedTemplate(null)
        setIsDuplicating(false)
        setLivePreviewContent(null)
    }

    const openModal = (template = null, duplicate = false) => {
        const catalogKeys = variableCatalog.map((v) => v.key)
        if (template) {
            setSelectedTemplate(duplicate ? null : template)
            setIsDuplicating(duplicate)
            setCustomVariables(splitCustomVariables(template.variables || [], catalogKeys))
            setFormData({
                name: duplicate ? `${template.name} (Copy)` : template.name,
                key: duplicate ? `${template.key}-copy` : template.key,
                content: template.content || "",
                description: template.description || "",
                category: template.category || "workflow",
                variables: template.variables || [],
                media_urls: template.media_urls || [],
                is_active: template.is_active,
            })
        } else {
            resetForm()
        }
        setIsModalOpen(true)
    }

    const closeModal = () => {
        setIsModalOpen(false)
        setTimeout(resetForm, 300)
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setIsSubmitting(true)

        const payload = {
            ...formData,
            variables: mergeTemplateVariables(
                customVariables,
                parseVariablesFromContent(formData.content),
            ),
            media_urls: (formData.media_urls || []).filter((url) => url && url.trim() !== ''),
        }

        try {
            if (isEditing && !isDuplicating) {
                await router.put(`/text-templates/${selectedTemplate.id}`, payload, {
                    preserveState: true,
                    preserveScroll: true,
                    onSuccess: () => {
                        toast({
                            title: "Success",
                            description: "Text template updated successfully.",
                        })
                        closeModal()
                    },
                    onError: (errors) => {
                        const errorMessage = errors.message || Object.values(errors).flat().join(', ') || "Failed to update text template."
                        toast({
                            title: "Error",
                            description: errorMessage,
                            variant: "destructive",
                        })
                    }
                })
            } else {
                await router.post('/text-templates', payload, {
                    preserveState: true,
                    preserveScroll: true,
                    onSuccess: () => {
                        toast({
                            title: "Success",
                            description: `Text template ${isDuplicating ? 'duplicated' : 'created'} successfully.`,
                        })
                        closeModal()
                    },
                    onError: (errors) => {
                        const errorMessage = errors.message || Object.values(errors).flat().join(', ') || `Failed to ${isDuplicating ? 'duplicate' : 'create'} text template.`
                        toast({
                            title: "Error",
                            description: errorMessage,
                            variant: "destructive",
                        })
                    }
                })
            }
        } catch (error) {
            toast({
                title: "Error",
                description: "An unexpected error occurred.",
                variant: "destructive",
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleDelete = async (template) => {
        if (!confirm(`Are you sure you want to delete "${template.name}"?`)) {
            return
        }

        try {
            await router.delete(`/text-templates/${template.id}`, {
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => {
                    toast({
                        title: "Success",
                        description: "Text template deleted successfully.",
                    })
                },
                onError: () => {
                    toast({
                        title: "Error",
                        description: "Failed to delete text template.",
                        variant: "destructive",
                    })
                }
            })
        } catch (error) {
            toast({
                title: "Error",
                description: "An unexpected error occurred.",
                variant: "destructive",
            })
        }
    }

    const handlePreview = async (template) => {
        try {
            const response = await fetch(`/text-templates/${template.id}/preview`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
                },
                body: JSON.stringify({})
            })
            
            const data = await response.json()
            setPreviewContent(data)
            setIsPreviewOpen(true)
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to generate preview.",
                variant: "destructive",
            })
        }
    }

    const syncFromSavedTemplate = (template) => {
        const catalogKeys = variableCatalog.map((v) => v.key)
        setCustomVariables(splitCustomVariables(template.variables || [], catalogKeys))
        setFormData((prev) => ({ ...prev, variables: template.variables || [] }))
        if (!isDuplicating) {
            setSelectedTemplate(template)
        }
    }

    const buildVariablesPayload = (nextCustomVariables) => ({
        ...formData,
        variables: mergeTemplateVariables(
            nextCustomVariables,
            parseVariablesFromContent(formData.content),
        ),
        media_urls: (formData.media_urls || []).filter((url) => url && url.trim() !== ''),
    })

    const persistCustomVariables = async (nextCustomVariables, successMessage) => {
        const previousCustomVariables = customVariables
        setCustomVariables(nextCustomVariables)

        const templateId = selectedTemplate?.id
        if (!templateId || isDuplicating) {
            return
        }

        setIsSavingVariables(true)

        try {
            await new Promise((resolve, reject) => {
                router.put(`/text-templates/${templateId}`, buildVariablesPayload(nextCustomVariables), {
                    preserveState: true,
                    preserveScroll: true,
                    only: ['templates'],
                    onSuccess: (page) => {
                        const updated = page.props.templates?.find((t) => t.id === templateId)
                        if (updated) {
                            syncFromSavedTemplate(updated)
                        }
                        toast({
                            title: "Success",
                            description: successMessage,
                        })
                        resolve(updated)
                    },
                    onError: (errors) => {
                        setCustomVariables(previousCustomVariables)
                        toast({
                            title: "Error",
                            description: errors.message || Object.values(errors).flat().join(', ') || "Failed to save custom variable.",
                            variant: "destructive",
                        })
                        reject(errors)
                    },
                })
            })
        } finally {
            setIsSavingVariables(false)
        }
    }

    const addCustomVariable = async (variable) => {
        await persistCustomVariables(
            [...customVariables, variable],
            "Custom variable added.",
        )
    }

    const removeCustomVariable = async (variableKey) => {
        await persistCustomVariables(
            customVariables.filter((v) => v.key !== variableKey),
            "Custom variable removed.",
        )
    }

    const insertVariableIntoContent = (variableText) => {
        const textarea = document.getElementById('content')
        if (textarea) {
            const start = textarea.selectionStart
            const end = textarea.selectionEnd
            const content = formData.content
            const newContent = content.substring(0, start) + variableText + content.substring(end)
            setFormData({ ...formData, content: newContent })
            
            setTimeout(() => {
                textarea.focus()
                textarea.setSelectionRange(start + variableText.length, start + variableText.length)
            }, 0)
        }
    }

    const addMediaUrl = () => {
        setFormData({ ...formData, media_urls: [...(formData.media_urls || []), ''] })
    }

    const updateMediaUrl = (index, value) => {
        const media_urls = [...(formData.media_urls || [])]
        media_urls[index] = value
        setFormData({ ...formData, media_urls })
    }

    const removeMediaUrl = (index) => {
        const media_urls = (formData.media_urls || []).filter((_, i) => i !== index)
        setFormData({ ...formData, media_urls })
    }

    const generateLivePreview = () => {
        if (!formData.content) {
            setLivePreviewContent(null)
            return
        }

        const sampleData = {
            ...getSampleVariableData(),
            ...buildSampleDataFromVariables(customVariables),
        }
        const previewContent = renderPreviewContent(formData.content, sampleData)
        setLivePreviewContent({
            content: previewContent,
            segments: estimateSmsSegments(previewContent),
            charCount: previewContent.length,
        })
    }

    // Generate live preview when content changes
    React.useEffect(() => {
        generateLivePreview()
    }, [formData.content, customVariables])

    const getDefaultTemplate = (templateType) => {
        const templates = {
            "user-welcome": `Hello {{ $user->firstName }},

Welcome to {{ $app_name }}! We're excited to have you on board.

Your account has been successfully created and you're just one step away from accessing our comprehensive platform. To get started, you'll need to create a secure password for your account.

Please click the link below to set up your password:
{{ $url }}/create-password/{{ $key }}

If you're unable to click the link, please copy and paste it into your web browser.

This link will expire in 24 hours for security reasons.

Best regards,
The {{ $app_name }} Team

© {{ $date }} {{ $app_name }}. All rights reserved.`,
            "password-reset": `Hello {{ $user->name }},

We received a request to reset your password for your {{ $app_name }} account.

If you made this request, please click the link below to reset your password:
{{ $url }}/reset-password/{{ $key }}

If you're unable to click the link, please copy and paste it into your web browser.

This link will expire in 60 minutes for security reasons. If you didn't request this password reset, you can safely ignore this message.

Need assistance? Our support team is here to help you. Don't hesitate to reach out if you have any questions.

Best regards,
The {{ $app_name }} Team

© {{ $date }} {{ $app_name }}. All rights reserved.`
        }
        return templates[templateType] || ""
    }

    const handleTemplateTypeChange = (templateType) => {
        const content = getDefaultTemplate(templateType)
        setFormData(prev => ({
            ...prev,
            content,
            key: templateType
        }))
    }

    return (
        <AuthenticatedLayout>
            <Head title="DME | Text Templates" />
            
            <div className="flex flex-col gap-6 pb-8 pt-6 px-4 md:px-6 w-full">
                <div className="flex flex-col gap-2">
                    <h1 className="text-3xl font-bold tracking-tight">Text Templates</h1>
                    <p className="text-muted-foreground">
                        Manage your text templates for SMS, notifications, and other text-based communications
                    </p>
                </div>

                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="flex gap-4 items-center">
                        <Input
                            placeholder="Search templates..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="max-w-sm"
                        />
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-[180px]">
                                <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="All Statuses">All Statuses</SelectItem>
                                <SelectItem value="Active">Active</SelectItem>
                                <SelectItem value="Inactive">Inactive</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                            <SelectTrigger className="w-[180px]">
                                <SelectValue placeholder="Category" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="All Categories">All Categories</SelectItem>
                                {Object.entries(categories).map(([key, label]) => (
                                    <SelectItem key={key} value={key}>{label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {getActiveFilterCount() > 0 && (
                            <Button variant="ghost" size="sm" onClick={resetFilters}>
                                <X className="mr-2 h-4 w-4" />
                                Reset
                            </Button>
                        )}
                    </div>
                </div>

                <div className="text-sm text-muted-foreground">
                    {filteredTemplates.length} template{filteredTemplates.length !== 1 ? "s" : ""} found
                </div>

                <Card>
                    <CardContent className="p-0">
                        <div className="rounded-md border">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-[80px]">ID</TableHead>
                                        <TableHead>Name</TableHead>
                                        <TableHead className="hidden md:table-cell">Key</TableHead>
                                        <TableHead className="hidden lg:table-cell">Category</TableHead>
                                        <TableHead className="hidden lg:table-cell">Content Preview</TableHead>
                                        <TableHead className="hidden lg:table-cell">Variables</TableHead>
                                        <TableHead className="hidden xl:table-cell">Status</TableHead>
                                        <TableHead className="hidden xl:table-cell">Updated</TableHead>
                                        <TableHead className="w-[50px]"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredTemplates.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={9} className="h-24 text-center">
                                                <div className="flex flex-col items-center gap-2">
                                                    <MessageSquare className="h-8 w-8 text-muted-foreground" />
                                                    <p className="text-muted-foreground">
                                                        {searchTerm || statusFilter !== "All Statuses" 
                                                            ? "No templates match your filters." 
                                                            : "No text templates found. Get started by creating your first text template."}
                                                    </p>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        filteredTemplates.map((template) => (
                                            <TableRow
                                                key={template.id}
                                                className="cursor-pointer hover:bg-muted/50"
                                                onClick={() => openModal(template)}
                                            >
                                                <TableCell>{template.id}</TableCell>
                                                <TableCell>
                                                    <div>
                                                        <div className="font-medium">{template.name}</div>
                                                        <div className="text-sm text-muted-foreground md:hidden">
                                                            {template.key}
                                                        </div>
                                                        {template.description && (
                                                            <div className="text-sm text-muted-foreground">
                                                                {template.description}
                                                            </div>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="hidden md:table-cell">
                                                    <code className="text-sm bg-muted px-2 py-1 rounded">
                                                        {template.key}
                                                    </code>
                                                </TableCell>
                                                <TableCell className="hidden lg:table-cell">
                                                    <Badge variant="outline">
                                                        {categories[template.category] || template.category || 'Uncategorized'}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="hidden lg:table-cell">
                                                    <div className="max-w-[200px] truncate text-sm text-muted-foreground" title={template.content}>
                                                        {template.content ? template.content.substring(0, 50) + '...' : 'No content'}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="hidden lg:table-cell">
                                                    <Badge variant="outline">
                                                        {template.variables?.length || 0} vars
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="hidden xl:table-cell">
                                                    <Badge variant="outline" className={template.is_active ? "bg-green-100 text-green-800" : "text-muted-foreground"}>
                                                        {template.is_active ? "Active" : "Inactive"}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="hidden xl:table-cell">
                                                    {template.updated_at}
                                                </TableCell>
                                                <TableCell onClick={(e) => e.stopPropagation()}>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button variant="ghost" className="h-8 w-8 p-0">
                                                                <span className="sr-only">Open menu</span>
                                                                <MoreVertical className="h-4 w-4" />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end">
                                                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                                            <DropdownMenuItem onClick={() => openModal(template)}>
                                                                <Edit className="mr-2 h-4 w-4" />
                                                                Edit Template
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem onClick={() => openModal(template, true)}>
                                                                <Copy className="mr-2 h-4 w-4" />
                                                                Duplicate
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem onClick={() => handlePreview(template)}>
                                                                <Eye className="mr-2 h-4 w-4" />
                                                                Preview
                                                            </DropdownMenuItem>
                                                            <DropdownMenuSeparator />
                                                            <DropdownMenuItem
                                                                className="text-red-600"
                                                                onClick={() => handleDelete(template)}
                                                            >
                                                                <Trash2 className="mr-2 h-4 w-4" />
                                                                Delete Template
                                                            </DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </CardContent>
                </Card>

                {/* Create/Edit Modal */}
                <Dialog open={isModalOpen} onOpenChange={closeModal}>
                    <DialogContent
                        className={TEXT_TEMPLATE_EDITOR_DIALOG_CLASS}
                        overlayClassName={TEMPLATE_MODAL_OVERLAY_CLASS}
                    >
                        <DialogHeader>
                            <DialogTitle>
                                {isDuplicating ? "Duplicate Template" : (isEditing ? "Edit Template" : "Create Template")}
                            </DialogTitle>
                            <DialogDescription>
                                {isDuplicating 
                                    ? "Create a copy of the existing template with modifications." 
                                    : (isEditing ? "Make changes to the text template." : "Create a new text template.")
                                }
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleSubmit}>
                            <Tabs defaultValue="basic" className="w-full">
                                <TabsList className={`grid w-full grid-cols-3 ${TEMPLATE_MODAL_TABS_LIST_CLASS}`}>
                                    <TabsTrigger value="basic" className={TEMPLATE_MODAL_TABS_TRIGGER_CLASS}>Basic Info</TabsTrigger>
                                    <TabsTrigger value="variables" className={TEMPLATE_MODAL_TABS_TRIGGER_CLASS}>Variables</TabsTrigger>
                                    <TabsTrigger value="preview" className={TEMPLATE_MODAL_TABS_TRIGGER_CLASS}>Preview</TabsTrigger>
                                </TabsList>
                                
                                <TabsContent value="basic" className="space-y-4">
                                    <div className="grid gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="name">Template Name</Label>
                                            <Input
                                                id="name"
                                                value={formData.name}
                                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                placeholder="e.g., Welcome SMS"
                                                required
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="key">Template Key</Label>
                                            <Input
                                                id="key"
                                                value={formData.key}
                                                onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                                                placeholder="e.g., user-welcome-sms"
                                                required
                                            />
                                            <p className="text-sm text-muted-foreground">
                                                Unique identifier used by the system to find this template. Use lowercase with hyphens.
                                            </p>
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="category">Category</Label>
                                            <Select
                                                value={formData.category}
                                                onValueChange={(value) => setFormData({ ...formData, category: value })}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select category" />
                                                </SelectTrigger>
                                                <SelectContent className={TEMPLATE_MODAL_SELECT_CONTENT_CLASS}>
                                                    {Object.entries(categories).map(([key, label]) => (
                                                        <SelectItem key={key} value={key}>{label}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="description">Description</Label>
                                            <Textarea
                                                id="description"
                                                value={formData.description}
                                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                                placeholder="Brief description of this template..."
                                                rows={3}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <Label htmlFor="content">Template Content</Label>
                                                <VariableInsertButton onInsert={insertVariableIntoContent} variables={insertVariables} />
                                            </div>
                                            <Textarea
                                                id="content"
                                                value={formData.content}
                                                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                                placeholder="Your text template content..."
                                                rows={10}
                                                required
                                            />
                                            <p className="text-sm text-muted-foreground">
                                                Use variables like {`{{ user.name }}`} to personalize your messages. Use Insert Variable above or add custom variables on the Variables tab.
                                            </p>
                                        </div>
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <Label>MMS Media URLs (optional)</Label>
                                                <Button type="button" variant="outline" size="sm" onClick={addMediaUrl}>
                                                    <Plus className="h-3 w-3 mr-1" /> Add URL
                                                </Button>
                                            </div>
                                            {(formData.media_urls || []).length === 0 ? (
                                                <p className="text-sm text-muted-foreground">No media URLs added.</p>
                                            ) : (
                                                (formData.media_urls || []).map((url, index) => (
                                                    <div key={index} className="flex gap-2">
                                                        <Input
                                                            value={url}
                                                            onChange={(e) => updateMediaUrl(index, e.target.value)}
                                                            placeholder="https://example.com/image.jpg"
                                                        />
                                                        <Button type="button" variant="ghost" size="icon" onClick={() => removeMediaUrl(index)}>
                                                            <X className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <Switch
                                                id="is_active"
                                                checked={formData.is_active}
                                                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                                            />
                                            <Label htmlFor="is_active">Active</Label>
                                            <p className="text-sm text-muted-foreground ml-2">
                                                Only active templates can be used by the system
                                            </p>
                                        </div>
                                        {!isEditing && !isDuplicating && (
                                            <div className="space-y-2">
                                                <Label htmlFor="template-type">Start with Template (Optional)</Label>
                                                <Select onValueChange={handleTemplateTypeChange}>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Choose a template type for default content" />
                                                    </SelectTrigger>
                                                    <SelectContent className={TEMPLATE_MODAL_SELECT_CONTENT_CLASS}>
                                                        <SelectItem value="user-welcome">User Welcome Text</SelectItem>
                                                        <SelectItem value="password-reset">Password Reset Text</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        )}
                                    </div>
                                </TabsContent>

                                <TabsContent value="variables" className="space-y-4">
                                    <TemplateVariablesTab
                                        catalog={variableCatalog}
                                        customVariables={customVariables}
                                        onAddCustom={addCustomVariable}
                                        onRemoveCustom={removeCustomVariable}
                                        isSaving={isSavingVariables}
                                        canPersist={isEditing && !isDuplicating}
                                    />
                                </TabsContent>
                                
                                <TabsContent value="preview" className="space-y-4">
                                    {livePreviewContent ? (
                                        <div className="space-y-4">
                                            <Alert>
                                                <Eye className="h-4 w-4" />
                                                <AlertDescription>
                                                    This is a live preview with sample data. Variables are replaced with example values.
                                                </AlertDescription>
                                            </Alert>
                                            
                                            <div className="space-y-4">
                                                <div className="flex gap-4 text-sm text-muted-foreground">
                                                    <span>{livePreviewContent.charCount} characters</span>
                                                    <span>{livePreviewContent.segments} SMS segment{livePreviewContent.segments !== 1 ? 's' : ''}</span>
                                                </div>
                                                <div className="space-y-2">
                                                    <Label className="text-sm font-medium">Content Preview</Label>
                                                    <div className="border rounded-lg p-4 bg-muted/30 max-h-96 overflow-y-auto">
                                                        <pre className="whitespace-pre-wrap text-sm font-mono">
                                                            {livePreviewContent.content}
                                                        </pre>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center py-12">
                                            <Eye className="mx-auto h-12 w-12 text-muted-foreground" />
                                            <h3 className="mt-2 text-sm font-semibold text-muted-foreground">No content to preview</h3>
                                            <p className="mt-1 text-sm text-muted-foreground">
                                                Add content in the Basic Info tab to see a live preview here.
                                            </p>
                                        </div>
                                    )}
                                </TabsContent>
                            </Tabs>
                            
                            <DialogFooter className="mt-6">
                                <Button type="button" variant="outline" onClick={closeModal}>
                                    Cancel
                                </Button>
                                <Button type="submit" disabled={isSubmitting}>
                                    {isSubmitting ? "Saving..." : (isDuplicating ? "Create Copy" : (isEditing ? "Update Template" : "Create Template"))}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>

                {/* Preview Modal */}
                <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
                    <DialogContent
                        className={TEXT_TEMPLATE_PREVIEW_DIALOG_CLASS}
                        overlayClassName={TEMPLATE_MODAL_OVERLAY_CLASS}
                    >
                        <DialogHeader>
                            <DialogTitle>Text Template Preview</DialogTitle>
                            <DialogDescription>
                                Preview of how the text will look to recipients with sample data.
                            </DialogDescription>
                        </DialogHeader>
                        {previewContent && (
                            <div className="space-y-4">
                                <div className="border rounded-lg p-4 max-h-[400px] overflow-y-auto bg-muted/30">
                                    <pre className="whitespace-pre-wrap text-sm font-mono">
                                        {previewContent.content}
                                    </pre>
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    <p><strong>Note:</strong> This preview uses sample data. Actual messages will contain real user information.</p>
                                </div>
                            </div>
                        )}
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setIsPreviewOpen(false)}>
                                Close
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </AuthenticatedLayout>
    )
}